import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { FileText, ArrowRight, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { TypeBadge, SeverityBadge, ConfidenceMeter } from "./Badges";
import { fileUrl } from "../lib/api";

const EventCard = ({ event, onConfirm, onReject, compact = false }) => {
  const navigate = useNavigate();
  const src = event.source || {};
  return (
    <div
      data-testid={`event-card-${event.id}`}
      className={`rounded-lg border bg-white dark:bg-slate-900 p-4 card-hover cursor-pointer ${
        event.needs_confirm ? "border-amber-300 dark:border-amber-500/40" : "border-slate-300 dark:border-slate-700"
      } hover:bg-slate-100 dark:hover:bg-slate-800`}
      onClick={() => navigate(`/events/${event.id}`)}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <TypeBadge type={event.event_type} />
        <SeverityBadge severity={event.severity} note={event.impact_note} />
        {event.needs_confirm && (
          <span
            data-testid="event-needs-confirm-chip"
            className="inline-flex items-center gap-1 rounded-md border border-amber-400 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-sm font-mono font-semibold text-amber-700 dark:text-amber-300"
          >
            <AlertTriangle className="h-3 w-3" /> Needs your confirm
          </span>
        )}
        <span className="ml-auto text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">
          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
        </span>
      </div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-snug">{event.title}</h3>
      {!compact && <p className="mt-1 text-base text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3">{event.summary}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
        <span className="font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{event.vendor_name}</span>
        {event.due_date && <span className="font-mono text-cyan-600 dark:text-cyan-400">due {event.due_date}</span>}
        <ConfidenceMeter value={event.confidence} />
        {src.storage_path && (
          <a
            data-testid="event-card-source-doc-btn"
            href={fileUrl(src.storage_path)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            <FileText className="h-3.5 w-3.5" /> Source doc
          </a>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
          Details <ArrowRight className="h-3 w-3" />
        </span>
      </div>
      {event.recommended_action && !compact && (
        <div className="mt-3 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-base text-slate-700 dark:text-slate-300">
          <span className="font-mono text-sm uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block mb-0.5">Recommended action</span>
          {event.recommended_action}
        </div>
      )}
      {event.needs_confirm && (onConfirm || onReject) && (
        <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
          {onConfirm && (
            <button
              data-testid="event-card-confirm-btn"
              onClick={() => onConfirm(event)}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-emerald-950 hover:bg-emerald-400 transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
            </button>
          )}
          {onReject && (
            <button
              data-testid="event-card-reject-btn"
              onClick={() => onReject(event)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <XCircle className="h-3.5 w-3.5" /> Reject
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCard;
